import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
import { isValidGoalDate } from "../pages/SalesGoal/Component/shared";
const SALES_GOAL_DATE_STORAGE_KEY = "sales_goal:selected_month";
const getSavedGoalDate = () => {
    try {
        const stored = sessionStorage.getItem(SALES_GOAL_DATE_STORAGE_KEY);
        if (stored && isValidGoalDate(stored)) {
            return stored;
        }
    }
    catch {
        // ignore sessionStorage errors
    }
    return new Date().toISOString().slice(0, 7);
};
/* initial states */
const initialState = {
    goals: [],
    loading: false,
    syncing: false,
    error: null,
    goal_date: getSavedGoalDate(),
    lastUpdated: null,
};
/* async thunk */
export const fetchSalesGoal = createAsyncThunk("salesGoal/fetchSalesGoal", async ({ goal_date }, { getState, rejectWithValue }) => {
    try {
        if (!isValidGoalDate(goal_date)) {
            return rejectWithValue("Invalid goal_date. Expected format: YYYY-MM");
        }
        const token = getState().auth?.token;
        const res = await ApiCall("GET", `/api/sales-goals?goal_date=${encodeURIComponent(goal_date)}`, null, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            return (res.data.data || []);
        }
        const backendMsg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to fetch sales goals";
        return rejectWithValue(backendMsg);
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err?.message || "Fetch sales goals error");
    }
});
export const syncSalesGoal = createAsyncThunk("salesGoal/syncSalesGoal", async ({ goal_date }, { getState, dispatch, rejectWithValue }) => {
    try {
        if (!isValidGoalDate(goal_date)) {
            const msg = "Invalid goal_date. Expected format: YYYY-MM";
            toast.error(msg);
            return rejectWithValue(msg);
        }
        const token = getState().auth?.token;
        const res = await ApiCall("POST", `/api/sales-goals/sync`, { goal_date }, { Authorization: `Bearer ${token}` });
        if (res && "data" in res && res.data?.success) {
            toast.success(res.data.message || "Sales goals synced successfully");
            dispatch(fetchSalesGoal({ goal_date }));
            return;
        }
        const msg = res?.response?.data?.message ||
            res?.data?.message ||
            "Failed to sync sales goals";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Sync sales goals failed";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
/* slice */
const salesGoalSlice = createSlice({
    name: "salesGoal",
    initialState,
    reducers: {
        setGoalDate: (state, action) => {
            if (isValidGoalDate(action.payload)) {
                state.goal_date = action.payload;
                try {
                    sessionStorage.setItem(SALES_GOAL_DATE_STORAGE_KEY, action.payload);
                }
                catch {
                    // ignore sessionStorage write failure
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSalesGoal.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchSalesGoal.fulfilled, (state, action) => {
            state.loading = false;
            state.goals = action.payload;
            // Latest updatedAt across rows = effective "last synced" (Mongoose bumps it on every sync upsert)
            state.lastUpdated = action.payload.reduce((acc, g) => {
                if (!g.updatedAt)
                    return acc;
                return !acc || new Date(g.updatedAt) > new Date(acc) ? g.updatedAt : acc;
            }, null);
        })
            .addCase(fetchSalesGoal.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "An error occurred";
            state.goals = [];
            state.lastUpdated = null;
        })
            .addCase(syncSalesGoal.pending, (state) => {
            state.syncing = true;
        })
            .addCase(syncSalesGoal.fulfilled, (state) => {
            state.syncing = false;
            // lastUpdated will refresh from the follow-up fetch (which reads it from DB)
        })
            .addCase(syncSalesGoal.rejected, (state) => {
            state.syncing = false;
        });
    },
});
/* selectors */
export const { setGoalDate } = salesGoalSlice.actions;
export const selectSalesGoal = (state) => state.salesGoal.goals;
export const selectSalesGoalLoading = (state) => state.salesGoal.loading;
export const selectSalesGoalSyncing = (state) => state.salesGoal.syncing;
export const selectSalesGoalError = (state) => state.salesGoal.error;
export const selectSalesGoalDate = (state) => state.salesGoal.goal_date;
export const selectSalesGoalLastUpdated = (state) => state.salesGoal.lastUpdated;
export default salesGoalSlice.reducer;
